import * as Yup from "yup";
import React, { useMemo } from "react";
import { useLocalization } from "../../../context/LocalizationContext";
const FILE_SIZE = 1024 * 1024; // 1 MB
const SUPPORTED_FORMATS = ["image/png", "image/jpeg", "image/jpg"];
export const createDealershipValidationSchema = (localization) =>
  Yup.object({
    logoPath: Yup.mixed()
      .required(localization("validation_logo_required"))
      .test("fileSize", localization("validation_logo_file_size"), (value) => {
        if (typeof value === "string") return true;
        return !value || (value && value.size <= FILE_SIZE);
      })
      .test(
        "fileFormat",
        localization("validation_logo_file_format"),
        (value) => {
          if (typeof value === "string") return true;
          return !value || (value && SUPPORTED_FORMATS.includes(value.type));
        },
      ),
    name: Yup.string()
      .required(localization("validation_name_required"))
      .matches(
        /^[A-Za-zÀ-ÖØ-öø-ÿ0-9&’'.,\-()/\s]+$/,
        localization("validation_name_invalid_chars"),
      )
      .test(
        "no-leading-trailing-spaces",
        localization("validation_name_no_leading_trailing_spaces"),
        (v) => v === (v || "").trim(),
      )
      .test(
        "no-multi-spaces",
        localization("validation_name_no_multi_spaces"),
        (v) => !/\s{2,}/.test(v || ""),
      )
      .test(
        "not-only-numbers",
        localization("validation_name_no_only_numbers"),
        (v) => !/^\d+$/.test(v || ""),
      )
      .min(2, localization("validation_name_min_length"))
      .max(50, localization("validation_name_max_length")),

    address1: Yup.string()
      .required(localization("validation_address1_required"))
      .matches(
        /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
        localization("validation_address1_invalid_chars"),
      )
      .test(
        "no-leading-trailing-spaces",
        localization("validation_address1_no_leading_trailing_spaces"),
        (v) => v === (v || "").trim(),
      )
      .test(
        "no-multi-spaces",
        localization("validation_address1_no_multi_spaces"),
        (v) => !/\s{2,}/.test(v || ""),
      )
      .test(
        "not-only-numbers",
        localization("validation_address1_no_only_numbers"),
        (v) => !/^\d+$/.test(v || ""),
      )
      .min(5, localization("validation_address1_min_length"))
      .max(50, localization("validation_address1_max_length")),

    address2: Yup.string()
      .notRequired()
      .matches(
        /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
        localization("validation_address2_invalid_chars"),
      )
      .test(
        "no-leading-trailing-spaces",
        localization("validation_address2_no_leading_trailing_spaces"),
        (v) => !v || v === v.trim(),
      )
      .test(
        "no-multi-spaces",
        localization("validation_address2_no_multi_spaces"),
        (v) => !v || !/\s{2,}/.test(v),
      )
      .test(
        "not-only-numbers",
        localization("validation_address2_no_only_numbers"),
        (v) => !v || !/^\d+$/.test(v),
      )
      .min(5, localization("validation_address2_min_length"))
      .max(50, localization("validation_address2_max_length")),

    address3: Yup.string()
      .notRequired()
      .matches(
        /^[A-Za-z0-9 .,'#\/()\-\s]*$/,
        localization("validation_address3_invalid_chars"),
      )
      .test(
        "no-leading-trailing-spaces",
        localization("validation_address3_no_leading_trailing_spaces"),
        (v) => !v || v === v.trim(),
      )
      .test(
        "no-multi-spaces",
        localization("validation_address3_no_multi_spaces"),
        (v) => !v || !/\s{2,}/.test(v),
      )
      .test(
        "not-only-numbers",
        localization("validation_address3_no_only_numbers"),
        (v) => !v || !/^\d+$/.test(v),
      )
      .min(5, localization("validation_address3_min_length"))
      .max(50, localization("validation_address3_max_length")),
    cityOrLocality: Yup.string()
      .trim(localization("validation_city_no_leading_trailing_spaces"))
      .required(localization("validation_city_required")),
    // .matches(/^[A-Za-z\- ]+$/, localization('validation_city_invalid_chars'))
    // .test('no-leading-trailing-spaces', localization('validation_city_no_leading_trailing_spaces'),
    //   v => v === (v || '').trim())
    // .test('no-multi-spaces', localization('validation_city_no_multi_spaces'),
    //   v => !(/\s{2,}/.test(v || '')))
    // .min(2, localization('validation_city_min_length'))
    // .max(50, localization('validation_city_max_length')),
    stateOrProvinceCode: Yup.string()
      .trim(localization("validation_state_no_leading_trailing_spaces"))
      .strict(true)
      // .min(2, localization('validation_state_min_length'))
      // .max(50, localization('validation_state_max_length'))
      // .matches(/^[A-Za-z .'-]+$/, localization('validation_state_invalid_chars'))
      .required(localization("validation_state_required")),
    postalCodeOrZip: Yup.string()
      .trim(localization("validation_postal_no_leading_trailing_spaces"))
      .strict(true)
      .required(localization("validation_postal_required"))
      .matches(
        /^\d{5}(-\d{4})?$/,
        localization("validation_postal_invalid_format"),
      ),
    timeZoneCode: Yup.string().required(
      localization("validation_timezone_required"),
    ),
    languageCode: Yup.string().required(
      localization("validation_language_required"),
    ),
    selectedServices: Yup.array()
      .min(1, localization("validation_services_required"))
      .required(localization("validation_services_required")),
    hourlyLabourRate: Yup.number()
      .typeError(localization("validation_hourly_labour_rate_must_be_number"))
      .positive(
        localization("validation_hourly_labour_rate_must_be_greater_than_zero"),
      )
      .max(100000, localization("validation_hourly_labour_rate_too_high"))
      .test(
        "max-two-decimals",
        localization("validation_hourly_labour_rate_max_two_decimals"),
        (value) =>
          value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
      )
      .required(localization("validation_hourly_labour_rate_required")),
    tax: Yup.number()
      .typeError(localization("validation_tax_must_be_number"))
      .positive(localization("validation_tax_must_be_greater_than_zero"))
      .max(100, localization("validation_tax_too_high"))
      .test(
        "max-two-decimals",
        localization("validation_tax_max_two_decimals"),
        (value) =>
          value === undefined || /^\d+(\.\d{1,2})?$/.test(String(value)),
      )
      .required(localization("validation_tax_required")),
  });

export const useDealershipValidationSchema = () => {
  const { t } = useLocalization();
  return useMemo(() => createDealershipValidationSchema(t), [t]);
};
